import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, X } from "lucide-react";
import sidebarConfig, { bottomConfig } from "./sidebarConfig";

/**
 * AdminSidebar
 * - Desktop: collapsible icon-only (64px) ↔ full (240px)
 * - Mobile: overlay drawer sliding in from left
 */
export default function AdminSidebar({
  viewMode,
  setViewMode,
  collapsed,
  setCollapsed,
  mobileOpen,
  setMobileOpen,
  onLogout,
  user,
}) {
  const [openGroups, setOpenGroups] = useState(["projects", "study", "math"]);

  // Automatically ensure parent groups of viewMode are open
  useEffect(() => {
    const findParents = (items, targetId, parents = []) => {
      for (const item of items) {
        if (item.id === targetId) return parents;
        if (item.children) {
          const res = findParents(item.children, targetId, [...parents, item.id]);
          if (res) return res;
        }
      }
      return null;
    };
    const parentIds = findParents(sidebarConfig, viewMode);
    if (parentIds && parentIds.length > 0) {
      setOpenGroups((prev) => Array.from(new Set([...prev, ...parentIds])));
    }
  }, [viewMode]);

  const toggleGroup = (id) => {
    setOpenGroups((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );
  };

  const handleNavClick = (item) => {
    if (item.children) {
      if (collapsed) setCollapsed(false);
      toggleGroup(item.id);
    } else {
      setViewMode(item.id);
      setMobileOpen(false);
    }
  };

  const renderNavItem = (item, level = 1) => {
    const Icon = item.icon;
    const hasChildren = Boolean(item.children && item.children.length > 0);
    const isOpen = openGroups.includes(item.id);
    // Only exact viewMode match displays active background
    const active = viewMode === item.id;

    const levelClass =
      level === 1
        ? "admin-nav-level1"
        : level === 2
        ? "admin-nav-level2"
        : "admin-nav-level3";

    return (
      <div key={item.id} className="admin-nav-group">
        <button
          type="button"
          onClick={() => handleNavClick(item)}
          className={`admin-nav-item ${levelClass} ${active ? "admin-nav-active" : ""}`}
          title={collapsed ? item.label : undefined}
        >
          {active && <span className="admin-nav-accent-bar" />}
          <span className={`admin-nav-icon ${level > 1 ? "admin-nav-icon--sm" : ""}`}>
            <Icon size={level === 1 ? 18 : level === 2 ? 16 : 14} strokeWidth={2} />
          </span>
          {!collapsed && (
            <span className={`admin-nav-label ${level > 1 ? "admin-nav-label--sm" : ""}`}>
              {item.label}
            </span>
          )}
          {!collapsed && hasChildren && (
            <span className={`admin-nav-chevron ${isOpen ? "admin-nav-chevron--open" : ""}`}>
              <ChevronRight size={14} />
            </span>
          )}
        </button>

        {/* Accordion Children */}
        {hasChildren && !collapsed && (
          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.div
                key={`submenu-${item.id}`}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{
                  height: { duration: 0.42, ease: [0.16, 1, 0.3, 1] },
                  opacity: { duration: 0.32, ease: [0.16, 1, 0.3, 1] },
                }}
                style={{ overflow: "hidden" }}
              >
                <motion.div
                  initial={{ y: -14, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -12, opacity: 0 }}
                  transition={{
                    y: { duration: 0.38, ease: [0.16, 1, 0.3, 1] },
                    opacity: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
                  }}
                >
                  {item.children.map((child) => renderNavItem(child, level + 1))}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    );
  };

  const SidebarContent = () => (
    <div className="admin-sidebar-inner">
      {/* Logo / Brand */}
      <div className="admin-sidebar-brand">
        <div className="admin-sidebar-logo">
          <span className="admin-sidebar-logo-icon">A</span>
        </div>
        {!collapsed && <span className="admin-sidebar-title">Admin Hub</span>}
      </div>

      {/* Divider */}
      <div className="admin-sidebar-divider" />

      {/* Navigation */}
      <nav className="admin-sidebar-nav">
        {sidebarConfig.map((item) => renderNavItem(item, 1))}
      </nav>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Bottom: User info + logout */}
      <div className="admin-sidebar-bottom">
        <div className="admin-sidebar-divider" />
        {bottomConfig.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              onClick={item.id === "logout" ? onLogout : undefined}
              className={`admin-nav-item ${item.danger ? "admin-nav-danger" : ""}`}
              title={collapsed ? item.label : undefined}
            >
              <span className="admin-nav-icon">
                <Icon size={18} strokeWidth={2} />
              </span>
              {!collapsed && <span className="admin-nav-label">{item.label}</span>}
            </button>
          );
        })}
        {!collapsed && user && (
          <div className="admin-sidebar-user">
            <div className="admin-sidebar-avatar">
              {(user.username || user.email || "A")[0].toUpperCase()}
            </div>
            <div className="admin-sidebar-user-info">
              <span className="admin-sidebar-user-name">
                {user.username || user.email || "Admin"}
              </span>
              <span className="admin-sidebar-user-role">Administrator</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* ── Desktop Sidebar ── */}
      <motion.aside
        className="admin-sidebar"
        animate={{ width: collapsed ? 64 : 240 }}
        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
      >
        <SidebarContent />
      </motion.aside>

      {/* ── Mobile Drawer ── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="admin-drawer-backdrop"
              onClick={() => setMobileOpen(false)}
            />
            {/* Drawer */}
            <motion.aside
              key="drawer"
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              className="admin-sidebar admin-sidebar--drawer"
              style={{ width: 240 }}
            >
              {/* Close button */}
              <button
                className="admin-drawer-close"
                onClick={() => setMobileOpen(false)}
              >
                <X size={18} />
              </button>
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
